import { useEffect, useState } from "react";
import { CheckCircle2, Download, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  useContextOperator,
  useDocTemplateCheck,
  useDocTemplates,
  useGeneratedDocs,
  useGenerateDocument,
  useTaskPolling,
} from "../hooks";
import { fmtDateTime } from "../utils";
import { operatorShort, pickTemplatesForOperator } from "../operatorTemplates";
import { TemplateFieldsPanel } from "./TemplateFieldsPanel";
import { DOC_TEMPLATE_KIND_LABELS, WPW_PACKAGE_KINDS } from "../types";
import type { ParcelOwner } from "../types";

// KIND = kontekst generowania (właściciel działki) — ten zostaje.
// ALLOWED_KINDS = rodzaje SZABLONÓW oferowane w tym oknie: rodzaj „Zgoda prywatna"
// został wycofany i zastąpiony pakietami WPW, więc wybiera się tu jeden z pakietów
// (karta informacyjna, formularze ENERGA per status, porozumienie TAURON). Szablony
// „bez typu" — w tym dawne zgody prywatne — nie są oferowane nigdzie.
const KIND = "private_consent";
const ALLOWED_KINDS: string[] = [...WPW_PACKAGE_KINDS, "general"];

/** Etykieta rodzaju w opcji selecta — bez powtarzanego prefiksu „Pakiet WPW — ". */
const kindLabel = (kind: string) =>
  (DOC_TEMPLATE_KIND_LABELS[kind as keyof typeof DOC_TEMPLATE_KIND_LABELS] ?? kind)
    .replace("Pakiet WPW — ", "");

// Generate a consent document for one person-kind parcel owner: pick a WPW package
// template, verify every #variable# it uses resolves in this context, render in
// the background. Only the LATEST render per owner is kept ("pobierz ostatnio
// wygenerowany").
export function GenerateConsentModal({ owner, onClose }: { owner: ParcelOwner; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: templates, isLoading: templatesLoading } = useDocTemplates();
  // Operator (OSD) projektu — pod niego dobieramy „niewspólne" szablony zgód.
  const { data: operatorInfo } = useContextOperator(KIND, owner.id);
  const operator = operatorInfo?.operator ?? "energa";
  // Szablony pakietów WPW (lub uniwersalne) posortowane pod operatora; miękko —
  // wariant innego operatora nadal dostępny jako fallback (z dopiskiem).
  const options = pickTemplatesForOperator(templates ?? [], ALLOWED_KINDS, operator);
  const [templateId, setTemplateId] = useState("");
  const selectedOption = options.find((o) => o.template.id === templateId);
  const fallbackWarning =
    selectedOption && !selectedOption.exact && !selectedOption.shared;
  const check = useDocTemplateCheck(templateId, KIND, owner.id);
  const generate = useGenerateDocument();
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [emptyMode, setEmptyMode] = useState<"blank" | "marker">("blank");
  // pola, których ręczne wartości zapiszą się też NA STAŁE do bazy (opt-in per pole)
  const [persist, setPersist] = useState<string[]>([]);
  const setOverride = (name: string, value: string) =>
    setOverrides((s) => ({ ...s, [name]: value }));
  const setPersistFor = (name: string, on: boolean) =>
    setPersist((s) => (on ? [...new Set([...s, name])] : s.filter((n) => n !== name)));
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const running = generate.isPending || (!!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? ""));

  const { data: lastDocs } = useGeneratedDocs(KIND, [owner.id]);
  const last = lastDocs?.[0];

  // A finished task means the row (and its file) changed — refresh every
  // generated-docs query so the owner card and this modal pick up the new file.
  useEffect(() => {
    if (taskId && taskStatus && ["success", "failure", "cancelled"].includes(taskStatus)) {
      qc.invalidateQueries({ queryKey: ["pp", "generated-docs"] });
    }
  }, [taskId, taskStatus, qc]);

  const start = () => {
    if (!templateId || !check.data?.ok) return;
    const cleanOverrides = Object.fromEntries(
      Object.entries(overrides).filter(([, v]) => v.trim() !== ""),
    );
    generate.mutate(
      {
        templateId, contextKind: KIND, contextId: owner.id, overrides: cleanOverrides, emptyMode,
        persist: persist.filter((n) => (overrides[n] ?? "").trim() !== ""),
      },
      { onSuccess: (r) => setTaskId(r.task_id) },
    );
  };

  const done = taskStatus === "success";
  const failed = taskStatus === "failure" || generate.isError;

  return (
    <Modal open onClose={onClose} title={`Generuj zgodę prywatną — ${owner.full_name}`}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Szablon dokumentu — pakiet WPW *</span>
          {templatesLoading ? (
            <div className="py-2"><Spinner className="h-4 w-4" /></div>
          ) : (
            <select
              className="input-base mt-1"
              value={templateId}
              disabled={running}
              onChange={(e) => { setTemplateId(e.target.value); setTaskId(null); }}
            >
              <option value="">— wybierz pakiet WPW —</option>
              {options.map((o) => (
                <option key={o.template.id} value={o.template.id}>
                  {o.template.name} — {kindLabel(o.template.kind)} {o.badge}
                </option>
              ))}
            </select>
          )}
          {!templatesLoading && options.length === 0 && (
            <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
              Brak szablonów pakietów WPW. Wgraj je w „Generacji dokumentów” — rodzaj
              „Pakiet WPW …”.
            </p>
          )}
        </label>
        {fallbackWarning && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Brak szablonu dla operatora {operatorShort(operator)} — użyto szablonu innego
            operatora. Wgraj wariant {operatorShort(operator)} lub oznacz szablon jako uniwersalny.
          </p>
        )}

        {/* availability check of the picked template in THIS context */}
        {templateId && (
          <div className="text-xs">
            {check.isLoading ? (
              <p className="flex items-center gap-2 text-[var(--color-text-muted)]">
                <Spinner className="h-3 w-3" /> Sprawdzanie dostępności zmiennych…
              </p>
            ) : check.data?.ok ? (
              <div className="space-y-1.5">
                <p className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <CheckCircle2 size={13} /> Wszystkie zmienne szablonu są dostępne w tym miejscu.
                </p>
                <TemplateFieldsPanel
                  fields={check.data.fields}
                  overrides={overrides}
                  onOverride={setOverride}
                  emptyMode={emptyMode}
                  onEmptyMode={setEmptyMode}
                  persist={persist}
                  onPersist={setPersistFor}
                  contextKind={KIND}
                  contextId={owner.id}
                  branchSetup={check.data.client_branch_setup}
                />
              </div>
            ) : check.data ? (
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-[var(--color-primary)]">
                  <XCircle size={13} /> Szablon używa zmiennych niedostępnych w tym miejscu:
                </p>
                <div className="flex flex-wrap gap-1">
                  {check.data.missing.map((v) => (
                    <span key={v} className="text-[11px] font-mono border border-[var(--color-border)] px-1.5 py-0.5 bg-[var(--color-bg-base)]">
                      #{v}#
                    </span>
                  ))}
                </div>
              </div>
            ) : check.isError ? (
              <p className="text-[var(--color-primary)]">Nie udało się sprawdzić szablonu.</p>
            ) : null}
          </div>
        )}

        {/* generation progress / result */}
        {taskId && (
          <div className="text-xs border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] p-2">
            {running ? (
              <p className="flex items-center gap-2 text-[var(--color-text-secondary)]">
                <Spinner className="h-3 w-3" /> Generowanie dokumentu…
              </p>
            ) : done ? (
              <div className="flex flex-wrap items-center gap-2">
                <p className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <CheckCircle2 size={13} /> Dokument wygenerowany.
                </p>
                {last?.file?.url && (
                  <a
                    href={last.file.url}
                    download={last.file.original_filename}
                    className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline"
                  >
                    <Download size={12} /> Pobierz
                  </a>
                )}
              </div>
            ) : (
              <p className="text-[var(--color-primary)]">
                Generowanie nie powiodło się{last?.error ? `: ${last.error}` : "."}
              </p>
            )}
          </div>
        )}
        {failed && !taskId && (
          <p className="text-xs text-[var(--color-primary)]">Nie udało się uruchomić generowania.</p>
        )}

        {/* last kept render for this owner */}
        {last?.status === "success" && last.file?.url && !done && (
          <p className="text-[11px] text-[var(--color-text-muted)]">
            Ostatnio wygenerowany: {last.template_name}
            {last.generated_at ? ` · ${fmtDateTime(last.generated_at)}` : ""} ·{" "}
            <a
              href={last.file.url}
              download={last.file.original_filename}
              className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
            >
              <Download size={11} /> pobierz
            </a>
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Zamknij</Button>
          <Button
            size="sm"
            disabled={!templateId || !check.data?.ok || running}
            loading={running}
            onClick={start}
          >
            Generuj
          </Button>
        </div>
      </div>
    </Modal>
  );
}
